import * as React from "react";
import { Box, Stack, Alert, AlertTitle, AlertColor } from "@mui/material";
import {
    useGetAssignedCoachesBySessionKeyQuery,
    useGetAvailableCoachesBySessionKeyQuery,
    useStoreCclParticipantCoachAssignmentsMutation,
} from "../../../../../services/cclTokenedEnterpriseCoachingApi";
import ComponentLoader from "../../../../../components/common/componentLoader";
import { PersonInfo } from "../../../../../models/personInfo";
import { useGetCclParticipantsByEventKeyQuery } from "../../../../../services/cclTokenedEnterpriseParticipantApi";
import {
    SessionDetailAssignedCoach,
    CoachAssignmentEndpointPayload,
} from "../../../../../services/types/sessionDetailTypes";
import NwaCoachAssignmentSubPanel from "./nwaCoachAssignmentSubPanel";
import { AccessEventSessionDetails } from "../../../../../services/types/accessEventTypes";
import { UserClaimsService } from "../../../../../services/currentUserService/currentUserService";
import { getErrorMsg } from "../../../../../utilities/rtkQueryErrorHelpers";
import {
    applyStoredCoachAssignments,
    getCoachAssignmentFailureMessage,
    getCoachAssignmentResultsFromError,
} from "../../../../../utilities/coachAssignmentResults";
import { useGetCclEventByEventKeyQuery } from "../../../../../services/cclTokenedSessionApi";
import useLogAccessEvent from "../../../../../hooks/useLogAccessEvent";
import CclAlertSnackbar from "../../../../../components/common/cclAlertSnackbar";

interface NwaSessionResourcesPanelProps {
    sessionId: string;
    setSaveNeeded: (needSave: boolean) => void;
}

const NwaSessionResourcesPanel: React.FC<NwaSessionResourcesPanelProps> = (props) => {
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>("info");
    const [saved, setSaved] = React.useState<boolean>(true);
    const [showLoader, setShowLoader] = React.useState<boolean>(false);
    const [modifiedAssignedCoaches, setModifiedAssignedCoaches] = React.useState<
        SessionDetailAssignedCoach[]
    >([]);
    const [storedAssignedCoaches, setStoredAssignedCoaches] = React.useState<
        SessionDetailAssignedCoach[]
    >([]);
    const [assignCoaches] = useStoreCclParticipantCoachAssignmentsMutation();
    const { logEvent } = useLogAccessEvent();
    const { data, isLoading, isSuccess, isError, error } = useGetAssignedCoachesBySessionKeyQuery(
        props.sessionId
    );
    const { data: availableCoaches, isSuccess: availableCoachesSuccess } =
        useGetAvailableCoachesBySessionKeyQuery(props.sessionId);
    const { data: allParticipants, isSuccess: participantsSuccess } =
        useGetCclParticipantsByEventKeyQuery(props.sessionId);
    const [sessionCode, setSessionCode] = React.useState<string>("");
    const { data: sessionDetail } = useGetCclEventByEventKeyQuery(props.sessionId, {
        skip: !props.sessionId,
    });

    const claimsService = new UserClaimsService();

    React.useEffect(() => {
        if (sessionDetail == null) return;
        setSessionCode(sessionDetail.sessionCode);
    }, [sessionDetail]);

    React.useEffect(() => {
        if (data == null) return;
        setStoredAssignedCoaches(data.coaches);
        setModifiedAssignedCoaches(data.coaches);
        if (availableCoachesSuccess) setShowLoader(false);
        // eslint-disable-next-line
    }, [data]);

    React.useEffect(() => {
        if (isSuccess && availableCoachesSuccess) setShowLoader(false);
        // eslint-disable-next-line
    }, [availableCoachesSuccess]);

    const ShowSnackbarElement = (message: string, severity: AlertColor) => {
        setShowSnackbar(true);
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
    };

    // Handlers
    const handleCoachAssignment = (
        paxImKey: number,
        coachImKey: number,
        isPrimary: boolean,
        assigned: boolean
    ) => {
        // see if we need to add or remove first
        const currentAssignment = modifiedAssignedCoaches.find(
            (ac) => ac.imKey === coachImKey && ac.paxImKey === paxImKey
        );
        const alreadyAssigned = currentAssignment != null;

        // handle cases where nothing needs to happen
        if (assigned === alreadyAssigned) return;

        // if remove we just pull the assignment from the existing assignment
        if (!assigned) {
            const newassignedcoaches = modifiedAssignedCoaches.filter(
                (ac) => !(ac.imKey === coachImKey && ac.paxImKey === paxImKey)
            );
            setModifiedAssignedCoaches(newassignedcoaches);
            return;
        }

        // add the new assignment
        const newcoach = availableCoaches?.coaches?.find((c) => c.imKey === coachImKey) ?? null;

        // safety - shouldn't be possible
        if (newcoach == null) return;

        // remove any assignments to other coaches from the assignment list
        let newassignedlist = isPrimary
            ? modifiedAssignedCoaches.filter((ac) => ac.paxImKey !== paxImKey)
            : [...modifiedAssignedCoaches];

        // don't care about the eskey here
        newassignedlist.push({
            imKey: coachImKey,
            pKey: newcoach.pKey,
            email: newcoach.email,
            firstName: newcoach.firstName,
            lastName: newcoach.lastName,
            isPrimary: isPrimary,
            isProposed: false,
            paxImKey: paxImKey,
            esKey: +props.sessionId,
        });
        setModifiedAssignedCoaches(newassignedlist);
    };

    React.useEffect(() => {
        const anyChanges = (): boolean => {
            const removedCoaches = storedAssignedCoaches.filter(
                (ac) =>
                    modifiedAssignedCoaches.find(
                        (ec) => ec.imKey === ac.imKey && ec.paxImKey === ac.paxImKey
                    ) == null
            );
            if (removedCoaches.length > 0) return true;

            const addedCoaches = modifiedAssignedCoaches.filter(
                (ac) =>
                    storedAssignedCoaches.find(
                        (ec) => ec.imKey === ac.imKey && ec.paxImKey === ac.paxImKey
                    ) == null
            );
            return addedCoaches.length > 0;
        };

        const changes = anyChanges();
        setSaved(!changes);
        props.setSaveNeeded(changes);
        // eslint-disable-next-line
    }, [modifiedAssignedCoaches]);

    const handleSave = () => {
        const removedCoaches = storedAssignedCoaches.filter(
            (ac) =>
                modifiedAssignedCoaches.find(
                    (ec) => ec.imKey === ac.imKey && ec.paxImKey === ac.paxImKey
                ) == null
        );
        const addedCoaches = modifiedAssignedCoaches.filter(
            (ac) =>
                storedAssignedCoaches.find(
                    (ec) => ec.imKey === ac.imKey && ec.paxImKey === ac.paxImKey
                ) == null
        );

        let endpointPayload: CoachAssignmentEndpointPayload = {
            eskey: +props.sessionId,
            payload: { coachAssignments: [] },
        };

        if (removedCoaches != null && removedCoaches.length > 0)
            removedCoaches.map((c) =>
                endpointPayload.payload.coachAssignments.push({
                    isRemove: true,
                    coachAssignment: {
                        coachImKey: c.imKey,
                        isPrimary: c.isPrimary,
                        isProposed: false,
                        paxImKey: c.paxImKey,
                    },
                })
            );

        if (addedCoaches != null && addedCoaches.length > 0)
            addedCoaches.map((c) =>
                endpointPayload.payload.coachAssignments.push({
                    isRemove: false,
                    coachAssignment: {
                        coachImKey: c.imKey,
                        isPrimary: c.isPrimary,
                        isProposed: false,
                        paxImKey: c.paxImKey,
                    },
                })
            );

        if (endpointPayload.payload.coachAssignments.length === 0) return;

        setShowLoader(true);
        assignCoaches(endpointPayload)
            .unwrap()
            .then((response) => {
                setShowLoader(false);

                // a resolved call is not enough - every requested change must have been stored
                const failureDetail = getCoachAssignmentFailureMessage(
                    response?.coachAssignmentResponses,
                    endpointPayload.payload.coachAssignments.length
                );
                if (failureDetail != null) {
                    // keep the baseline in step with the changes that were stored, so later edits diff correctly
                    setStoredAssignedCoaches((stored) =>
                        applyStoredCoachAssignments(
                            stored,
                            modifiedAssignedCoaches,
                            response?.coachAssignmentResponses ?? []
                        )
                    );
                    ShowSnackbarElement(`Coach Assignment Update Failed. ${failureDetail}`, "error");
                    return;
                }

                const evtData: AccessEventSessionDetails = {
                    sessionID: props.sessionId?.toString() ?? "",
                    projectCode: sessionCode,
                };

                logEvent("ResourceAssignmentsSaved", evtData);
                ShowSnackbarElement("Coach Assignments Updated", "success");
            })
            .catch((error) => {
                setShowLoader(false);
                const failedResults = getCoachAssignmentResultsFromError(error);
                if (failedResults != null)
                    setStoredAssignedCoaches((stored) =>
                        applyStoredCoachAssignments(stored, modifiedAssignedCoaches, failedResults)
                    );
                const detail =
                    failedResults != null
                        ? getCoachAssignmentFailureMessage(
                              failedResults,
                              endpointPayload.payload.coachAssignments.length
                          )
                        : null;
                ShowSnackbarElement(
                    detail
                        ? `Coach Assignment Update Failed. ${detail}`
                        : "Coach Assignment Update Failed",
                    "error"
                );
            });
    };

    const removeCoachsParticipants = (coach: PersonInfo, isPrimary: boolean) => {
        const newassignments = modifiedAssignedCoaches.filter(
            (c) => !(c.isPrimary === isPrimary && c.imKey === coach.imKey)
        );
        setModifiedAssignedCoaches(newassignments);
    };

    const assignAllToCoach = (coach: PersonInfo) => {
        if (participantsSuccess && allParticipants != null && allParticipants.length > 0) {
            // first remove all existing assignments/removals
            // NOTE this is only done for non-primary coaches, operation not allowed for primary coaches
            let newassignments = modifiedAssignedCoaches.filter((c) => c.imKey !== coach.imKey);
            const coachinfo = availableCoaches?.coaches?.find((c) => c.imKey === coach.imKey);
            if (coachinfo != null) {
                allParticipants
                    .filter((p) => p.registrationStatus === "Confirmed")
                    .map((p) =>
                        newassignments.push({
                            imKey: coachinfo.imKey,
                            pKey: coachinfo.pKey,
                            email: coachinfo.email,
                            firstName: coachinfo.firstName,
                            lastName: coachinfo.lastName,
                            isPrimary: false,
                            isProposed: false,
                            paxImKey: p.imKey,
                            esKey: +props.sessionId,
                        })
                    );
                setModifiedAssignedCoaches(newassignments);
            }
        }
    };

    function showErrorAlert(msg: string | undefined) {
        return (
            <Alert severity="error" sx={{ fontSize: 14 }}>
                <AlertTitle sx={{ fontSize: 14 }}>Error</AlertTitle>
                {msg}
            </Alert>
        );
    }

    if (isLoading) {
        return <ComponentLoader msg="" />;
    }

    if (isSuccess) {
        return (
            <Stack height={1} width={1}>
                {showSnackbar ? (
                    <CclAlertSnackbar
                        open={true}
                        onClose={() => setShowSnackbar(false)}
                        message={snackbarMessage}
                        severity={snackbarSeverity}
                    />
                ) : null}
                <Box
                    sx={{
                        width: 1,
                        m: 2,
                        height: "100%",
                        borderWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {claimsService.IsReadOnly() ? null : (
                        <NwaCoachAssignmentSubPanel
                            sessionId={props.sessionId}
                            assignedCoaches={modifiedAssignedCoaches ?? []}
                            updateCoachAssignment={handleCoachAssignment}
                            removeCoachesAssignments={removeCoachsParticipants}
                            assignAllToCoach={assignAllToCoach}
                            enableSave={!saved}
                            handleSave={handleSave}
                            saveInProgress={showLoader}
                        />
                    )}
                </Box>
            </Stack>
        );
    }

    if (isError) {
        return showErrorAlert(
            `Error retrieving participant assignment data for session ${
                props.sessionId
            } (${getErrorMsg(error)})`
        );
    }

    return showErrorAlert(
        `Unknown error retrieving participant assignment data for session ${props.sessionId}`
    );
};
export default NwaSessionResourcesPanel;
